import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import type { ComputeResourceType } from '@cast/shared-types';
import {
  OptimizationContainer,
  OptimizationWorkload,
  WorkloadManager,
  WorkloadOptimizationState,
  WorkloadOverrides,
  WorkloadRuntime,
  WorkloadScalingPolicy,
} from '@cast/types';
import { toBig } from '@cast/utils';

import { WORKLOAD_RECOMMENDATION_MIN_CONFIDENCE } from 'features/workload-optimization/constants';
import {
  OptimizationType,
  OptimizationTypeState,
  PreparedContainer,
  PreparedWorkload,
  ResourceMetrics,
  ResourceRecommendations,
  WorkloadRecommendation,
} from 'features/workload-optimization/types';

const isDefined = (...args: any[]): boolean => {
  return args.some((arg) => arg !== undefined);
};

const isVerticalRightsizingEnabled = (
  managementOption: WorkloadOptimizationState
): boolean => managementOption === WorkloadOptimizationState.MANAGED;

const isHorizontalRightsizingEnabled = (
  managementOption: WorkloadOptimizationState
): boolean => managementOption === WorkloadOptimizationState.MANAGED;

export const getOptimizationTypeState = ({
  isVerticalScalingEnabled,
  isHorizontalScalingEnabled,
  isHpaOverridden,
  isVpaOverridden,
}: {
  isVerticalScalingEnabled: boolean;
  isHorizontalScalingEnabled: boolean;
  isHpaOverridden: boolean;
  isVpaOverridden: boolean;
}): OptimizationTypeState => {
  if (!isVerticalScalingEnabled && !isHorizontalScalingEnabled) {
    return {
      type: OptimizationType.OFF,
      isOverridden: isHpaOverridden || isVpaOverridden,
    };
  }

  if (isVerticalScalingEnabled && isHorizontalScalingEnabled) {
    return {
      type: OptimizationType.BOTH,
      isOverridden: isHpaOverridden || isVpaOverridden,
    };
  }

  if (isVerticalScalingEnabled) {
    return { type: OptimizationType.VERTICAL, isOverridden: isVpaOverridden };
  }

  return { type: OptimizationType.HORIZONTAL, isOverridden: isHpaOverridden };
};

export const isOptimizationRightsizingOverridden = (
  workloadOverrides: WorkloadOverrides | undefined
): {
  isVpaOverridden: boolean;
  isHpaOverridden: boolean;
} => {
  return {
    isVpaOverridden: isDefined(workloadOverrides?.vertical?.managementOption),
    isHpaOverridden: isDefined(workloadOverrides?.horizontal?.managementOption),
  };
};

export const isManagedByAnnotations = ({
  managedBy,
}: Pick<OptimizationWorkload, 'managedBy'>): boolean =>
  managedBy === WorkloadManager.ANNOTATIONS;

const isLowConfidence = ({ recommendation }: OptimizationWorkload): boolean =>
  (recommendation?.confidence || 0) < WORKLOAD_RECOMMENDATION_MIN_CONFIDENCE;

export const isJvmContainer = (container: OptimizationContainer): boolean =>
  container.runtime === WorkloadRuntime.JVM;

export const getContainerResourceRecommendations = ({
  resources,
  recommendation,
  originalResources,
}: OptimizationContainer): Record<
  `${ComputeResourceType}`,
  ResourceRecommendations
> => {
  return {
    cpu: {
      requested: resources?.requests?.cpuCores ?? undefined,
      recommended: recommendation?.requests?.cpuCores ?? undefined,
      originalRequests: originalResources?.requests?.cpuCores ?? undefined,
    },
    memory: {
      requested: resources?.requests?.memoryGib ?? undefined,
      recommended: recommendation?.requests?.memoryGib ?? undefined,
      originalRequests: originalResources?.requests?.memoryGib ?? undefined,
    },
  };
};

type ReduceContainersArgs = {
  containers: PreparedContainer[];
  type: `${ComputeResourceType}`;
  podCount: number;
  isReadOnly: boolean;
};

export const reduceContainers = ({
  containers,
  type,
  podCount,
  isReadOnly,
}: ReduceContainersArgs): ResourceMetrics => {
  const reducedData = containers.reduce(
    (acc, curr) => {
      return {
        originalRequests:
          curr[type].originalRequests != null
            ? (acc.originalRequests || 0) + curr[type].originalRequests
            : acc.originalRequests,
        requested:
          curr[type].requested != null
            ? (acc.requested || 0) + curr[type].requested
            : acc.requested,
        recommended:
          curr[type].recommended != null
            ? (acc.recommended || 0) + curr[type].recommended
            : acc.recommended,
      };
    },
    {
      originalRequests: undefined,
      requested: undefined,
      recommended: undefined,
    } as ResourceRecommendations
  );
  return {
    ...reducedData,
    diff:
      reducedData.recommended != null && podCount
        ? (reducedData.recommended - (reducedData.requested ?? 0)) * podCount
        : undefined,
    currentDiff:
      reducedData.requested != null && podCount
        ? (reducedData.requested - (reducedData.originalRequests ?? 0)) *
          podCount
        : undefined,
    isReadOnly,
  };
};

export const prepareWorkload = (
  workload: OptimizationWorkload,
  policies: WorkloadScalingPolicy[]
): PreparedWorkload => {
  const scalingPolicy = policies.find(
    ({ id }) => id === workload.scalingPolicyId
  );
  const isCpuReadOnly =
    scalingPolicy?.recommendationPolicies.cpu.managementOption ===
    WorkloadOptimizationState.READ_ONLY;
  const isMemoryReadOnly =
    scalingPolicy?.recommendationPolicies.memory.managementOption ===
    WorkloadOptimizationState.READ_ONLY;
  const excludedContainerNames = new Set(
    workload.workloadConfigV2?.vpaConfig?.excludedContainers ?? []
  );
  const containers: PreparedContainer[] = workload.containers.map(
    (container) => {
      const { cpu, memory } = getContainerResourceRecommendations(container);
      const isExcluded = excludedContainerNames.has(container.name);
      return {
        ...container,
        cpu: {
          ...cpu,
          diff:
            !isExcluded && cpu.recommended != null
              ? (cpu.recommended - (cpu.requested ?? 0)) * workload.podCount
              : undefined,
          isReadOnly: isCpuReadOnly,
        },
        memory: {
          ...memory,
          diff:
            !isExcluded && memory.recommended != null
              ? (memory.recommended - (memory.requested ?? 0)) *
                workload.podCount
              : undefined,
          isReadOnly: isMemoryReadOnly,
        },
      };
    }
  );
  const containersForDiff = containers.filter(
    (container) => !excludedContainerNames.has(container.name)
  );

  const isJvmWorkload = workload.containers.some(isJvmContainer);

  const estimatedMonthlySavings =
    workload.costsPerHour?.requested && workload.costsPerHour?.recommended
      ? toBig(workload.costsPerHour.requested)
          .sub(workload.costsPerHour.recommended)
          .mul(AVERAGE_HOURS_IN_MONTH)
          .toNumber()
      : undefined;

  const currentMonthlySavings =
    workload.costsPerHour?.requested && workload.costsPerHour?.originalRequested
      ? toBig(workload.costsPerHour.originalRequested)
          .sub(workload.costsPerHour.requested)
          .mul(AVERAGE_HOURS_IN_MONTH)
          .toNumber()
      : undefined;

  const recommendation: WorkloadRecommendation = {
    events: [],
    deployed: false,
    ...workload.recommendation,
    confidence: workload.recommendation?.confidence ?? 0,
    lowConfidence: isLowConfidence(workload),
  };
  const isVerticalScalingEnabled = isVerticalRightsizingEnabled(
    workload.workloadConfigV2.vpaConfig.managementOption
  );
  const isHorizontalScalingEnabled = isHorizontalRightsizingEnabled(
    workload.workloadConfigV2.hpaConfig.managementOption
  );
  const { isHpaOverridden, isVpaOverridden } =
    isOptimizationRightsizingOverridden(workload.workloadOverrides);

  return {
    ...workload,
    isJvmWorkload,
    estimatedMonthlySavings,
    currentMonthlySavings,
    containers,
    verticalScalingPolicy: scalingPolicy,
    optimizationTypeState: getOptimizationTypeState({
      isVerticalScalingEnabled,
      isHorizontalScalingEnabled,
      isHpaOverridden,
      isVpaOverridden,
    }),
    isVerticalScalingEnabled: isVerticalRightsizingEnabled(
      workload.workloadConfigV2.vpaConfig.managementOption
    ),
    isHorizontalScalingEnabled: isHorizontalRightsizingEnabled(
      workload.workloadConfigV2.hpaConfig.managementOption
    ),
    isTypeSupportedByHpa: ['Deployment', 'StatefulSet', 'Rollout'].includes(
      workload.kind
    ),
    isManagedByAnnotations: isManagedByAnnotations(workload),
    recommendation,
    cpu: reduceContainers({
      containers: containersForDiff,
      type: 'cpu',
      podCount: workload.podCount,
      isReadOnly: isCpuReadOnly,
    }),
    memory: reduceContainers({
      containers: containersForDiff,
      type: 'memory',
      podCount: workload.podCount,
      isReadOnly: isMemoryReadOnly,
    }),
  };
};

export const getBiggestContainer = (
  containers: PreparedContainer[]
): PreparedContainer | undefined => {
  if (!containers.length) return undefined;

  return containers.reduce((biggest, current) => {
    const biggestCpu = biggest.cpu.requested ?? 0;
    const currentCpu = current.cpu.requested ?? 0;
    if (currentCpu !== biggestCpu) {
      return currentCpu > biggestCpu ? current : biggest;
    }
    const biggestMemory = biggest.memory.requested ?? 0;
    const currentMemory = current.memory.requested ?? 0;
    return currentMemory > biggestMemory ? current : biggest;
  });
};
